import type { ReactNode } from "react";
import type { Deadline } from "../api/types.js";
import { Async } from "../components/Async.jsx";
import { UrgencyBadge } from "../components/Chips.jsx";
import { IconState } from "../components/Icons.jsx";
import { usePortal } from "../app/context.js";
import { useAsync } from "../app/use-async.js";
import { href } from "../app/router.js";
import {
  URGENCY_HEADING,
  groupByUrgency,
  ruleFor,
  startedOn,
  summarise,
} from "../lib/deadlines.js";
import { isoDay, longDate } from "../lib/format.js";

/**
 * The compliance board.
 *
 * Every row says which rule it is enforcing and where that rule comes from.
 * That is the difference between software that nags and software an installer
 * can defend at an assessment: "12 days left" is a number, whereas "12 days
 * left of the 30-day MCS certificate window, measured from commissioning, under
 * IOR §4.10" is an argument.
 */
export function Deadlines(): ReactNode {
  const { client } = usePortal();
  const { state, reload } = useAsync<Deadline[]>(() => client.deadlines(), [client]);

  return (
    <>
      <div className="page-header spread">
        <h1>Compliance deadlines</h1>
        <button className="secondary" onClick={reload}>
          Refresh
        </button>
      </div>

      <Async state={state} what="the deadline board" reload={reload}>
        {(deadlines) => <Board deadlines={deadlines} />}
      </Async>
    </>
  );
}

function Board({ deadlines }: { readonly deadlines: readonly Deadline[] }): ReactNode {
  const groups = groupByUrgency(deadlines);

  return (
    <>
      {/* The summary is the live region: somebody using a screen reader should
          hear "3 outstanding: 1 overdue, 2 due soon" without touring the table. */}
      <p className="lede" role="status" aria-live="polite">
        {summarise(deadlines)}
      </p>

      {groups.length === 0 ? (
        <div className="state state--empty">
          <span className="state__glyph">
            <IconState kind="empty" />
          </span>
          <p className="state__title">No clock is running</p>
          <p className="state__body">
            Nothing outstanding. Clocks start when a job is commissioned or
            installed, so an empty board means no job is inside a window.
          </p>
        </div>
      ) : null}

      {groups.map((group) => (
        <section key={group.urgency}>
          <h2 className={`band band-${group.urgency}`}>
            {URGENCY_HEADING[group.urgency]}{" "}
            <span className="count">{group.deadlines.length}</span>
          </h2>
          <div className="table-scroll">
            <table>
              <caption className="visually-hidden">
                {URGENCY_HEADING[group.urgency]}
              </caption>
              <thead>
                <tr>
                  <th scope="col">Job</th>
                  <th scope="col">Obligation</th>
                  <th scope="col">Due</th>
                  <th scope="col">Status</th>
                </tr>
              </thead>
              <tbody>
                {group.deadlines.map((deadline) => (
                  <Row
                    key={`${deadline.job_id}:${deadline.kind}`}
                    deadline={deadline}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ))}
    </>
  );
}

function Row({ deadline }: { readonly deadline: Deadline }): ReactNode {
  const rule = ruleFor(deadline.kind);
  const started = startedOn(deadline);

  return (
    <tr className={deadline.days_remaining < 0 ? "is-breached" : undefined}>
      <th scope="row">
        <a href={href.job(deadline.job_id)}>{deadline.reference}</a>
      </th>
      <td>
        {rule.label}
        <br />
        <span className="standard small">{rule.authority}</span>
        <br />
        <span className="muted small">
          {rule.windowDays === null
            ? `Measured from ${rule.measuredFrom}; the window length is not something the API tells us.`
            : `${rule.windowDays} days from ${rule.measuredFrom}${
                started === null ? "" : `, which was ${longDate(started)}`
              }.`}
        </span>
      </td>
      <td className="ts">{isoDay(deadline.due_at)}</td>
      <td>
        <UrgencyBadge daysRemaining={deadline.days_remaining} />
      </td>
    </tr>
  );
}
